import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
} from '@mui/material';
import { BarStack, Bar, Line } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import PropTypes from 'prop-types';
import _ from 'lodash';

import YearSelector from '../../../../Common/Selectors/YearSelectorSingle';
import {
  getParams,
  thousandFormat,
  thousandFormat2,
} from '../../../../Utils/Functions';
import {
  positiveActiveColor,
  positiveDisableColor,
  minusActiveColor,
  minusDisableColor,
} from '../../../../Assets/js/constant';

let tooltipTimeout;

const finiteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const arraysEqual = (left = [], right = []) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
};

const normalizeProfitType = (value) =>
  (value || '').toString().trim().toLowerCase();

const isDirectorProfitType = (value) =>
  normalizeProfitType(value) === 'director';

const isPeopleProfitType = (value) =>
  ['director', 'projectmanager', 'supervisor', 'projectsourceone', 'projectsourcetwo'].includes(
    normalizeProfitType(value),
  );

const getInitialsFromRows = (rows = []) =>
  rows
    .map(
      (row) =>
        row?.DirectorInitials ||
        row?.DirectorInitial ||
        row?.EmployeeInitials ||
        row?.Initials ||
        '',
    )
    .find((value) => value && value.toString().trim()) || '';

const TopChart = ({
  profitType,
  label,
  selectedYears,
  selectedTopItems,
  summaryData,
  getProjectPerformanceSummary,
  getProjectPerformanceDetail,
  handleFilter,
  defaultStartMonth,
}) => {
  const isMounted = useRef(false);
  const svgRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const {
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [chatData, setChatData] = useState([]);
  const [xTransform, setXTransform] = useState(0);
  const [X, setX] = useState(0);
  const [iX, setIX] = useState(0);
  const [iY, setIY] = useState(0);
  const [rX, setRX] = useState(0);
  const [rY, setRY] = useState(0);
  const [rW, setRW] = useState(0);
  const [rH, setRH] = useState(0);
  const [canvasIndex, setCanvasIndex] = useState(-1);
  const [dragFlag, setDragFlag] = useState(false);
  const [minData, setMinData] = useState(0);
  const [maxData, setMaxData] = useState(0);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const onResize = () => {
    if (isMounted.current) setResize((prev) => !prev);
  };

  useEffect(() => {
    isMounted.current = true;
    prepareData();
    window.addEventListener('resize', onResize);
    return () => {
      isMounted.current = false;
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      prepareData();
    }
  }, [summaryData, profitType]);

  const prepareData = () => {
    let _data = [],
      maxData = 0,
      minData = 0,
      subField = 'ActualProfitYTD';

    if (profitType !== '' && summaryData) {
      let filterData = summaryData.filter((data) => data[subField]);
      let filter_data = _.mapValues(
        _.groupBy(filterData, profitType),
        (tlist) => tlist.map((data) => _.omit(data, profitType)),
      );

      Object.keys(filter_data).forEach((key) => {
        const name = key ? key : 'undefined';
        let item = {
          name,
          displayName: name,
          directorInitials: isDirectorProfitType(profitType)
            ? getInitialsFromRows(filter_data[key])
            : '',
          value: 0,
        };
        filter_data[key].forEach((data) => (item.value += data[subField]));
        _data.push(item);
      });

      if (isDirectorProfitType(profitType) && _data.length > 12) {
        _data = _data.map((item) => ({
          ...item,
          displayName: item.name,
        }));
      }
    }

    if (_data.length > 0) {
      minData = _data.reduce((min, obj) => {
        return obj.value < min ? obj.value : min;
      }, Infinity);
      maxData = _data.reduce((max, obj) => {
        return obj.value > max ? obj.value : max;
      }, -Infinity);

      let maxYValue = maxData > Math.abs(minData) ? maxData : minData;
      let minYValue = minData < Math.abs(maxData) ? minData : maxData;
      let transform = 0,
        maxTransform = 0,
        minTransform = 0;

      if (
        !isFinite(maxYValue) ||
        maxYValue === Number.NEGATIVE_INFINITY ||
        isNaN(maxYValue) ||
        maxYValue === 0
      )
        maxTransform = 30;
      else {
        if (parseInt(thousandFormat(maxYValue)[0]) > 7)
          maxTransform = 7.5 * (thousandFormat2(maxYValue).length + 1) + 10;
        else maxTransform = 7.5 * thousandFormat2(maxYValue).length + 10;
      }

      if (
        !isFinite(minYValue) ||
        minYValue === Number.NEGATIVE_INFINITY ||
        isNaN(minYValue) ||
        minYValue === 0
      )
        minTransform = 30;
      else {
        if (parseInt(thousandFormat(minYValue)[0]) > 7)
          minTransform = 7.5 * (thousandFormat2(minYValue).length + 1) + 10;
        else minTransform = 7.5 * thousandFormat2(minYValue).length + 10;
      }

      transform = Math.max(maxTransform, minTransform);
      setXTransform(transform);
    }

    setChatData(_data);
    setMaxData(maxData);
    setMinData(minData);
  };

  const getLocalCoords = (event) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - margin.left,
      y: event.clientY - rect.top - margin.top,
    };
  };

  const resetDrag = () => {
    setX(0);
    setIX(0);
    setIY(0);
    setRX(0);
    setRY(0);
    setRW(0);
    setRH(0);
    setCanvasIndex(-1);
    setDragFlag(false);
    setIsMouseDown(false);
  };

  const selectByDrag = () => {
    if (!chatData.length) return;

    const rectLeft = Math.min(rX, rX + rW);
    const rectRight = Math.max(rX, rX + rW);
    const selectedData = sortedChatData.filter((item) => {
      const bandLeft = xScale(item.name) ?? 0;
      const bandRight = bandLeft + xScale.bandwidth();
      return bandLeft < rectRight && bandRight > rectLeft;
    });

    handleFilter({
      selectedTopItems: selectedData.map((item) => item.name),
    });
  };

  const onSvgMouseDown = (event) => {
    if (event.button !== 0 || !svgRef.current) return;

    event.preventDefault();

    const target = event.target;
    if (
      target &&
      (target.getAttribute('data-no-drag') === 'true' ||
        (target.closest && target.closest('[data-no-drag="true"]')))
    ) {
      return;
    }

    const pos = getLocalCoords(event);
    dragStartRef.current = pos;
    setIsMouseDown(true);
    setX(pos.x);
    setIX(pos.x);
    setIY(pos.y);
    setRX(pos.x);
    setRY(pos.y);
    setRW(0);
    setRH(0);
    setDragFlag(true);
  };

  const onSvgMouseMove = (event) => {
    if (!isMouseDown || !dragFlag || event.buttons !== 1) return;

    const pos = getLocalCoords(event);
    const start = dragStartRef.current;
    setX(pos.x);
    setRX(Math.min(start.x, pos.x));
    setRY(Math.min(start.y, pos.y));
    setRW(Math.abs(start.x - pos.x));
    setRH(Math.abs(start.y - pos.y));
  };

  const onSvgMouseUp = () => {
    const dragThreshold = 5;
    const didDrag = rW > dragThreshold || rH > dragThreshold;

    setIsMouseDown(false);
    if (!dragFlag) return;

    if (didDrag) {
      selectByDrag();
    } else {
      deSelectAll();
    }

    resetDrag();
  };

  const onSvgMouseLeave = () => {
    setIsMouseDown(false);
    if (dragFlag) resetDrag();
  };

  const handleYear = (event) => {
    handleFilter({
      selectedYears: event.selectedYears,
      label: event.selectedYears.toString(),
    });
    handleFilter({
      selectedYears: [event.selectedYears],
      label: event.selectedYears[0].toString(),
    });
    let dimDateKey =
      (event.selectedYears[0] - 1).toString() +
      '0' +
      defaultStartMonth.toString() +
      '01';
    getProjectPerformanceSummary(event.selectedYears[0], dimDateKey);
    getProjectPerformanceDetail(event.selectedYears[0], dimDateKey);
  };

  const handleProfitTypeFilter = (event) => {
    handleFilter({
      profitType: event.target.value,
      selectedTopItems: [],
    });
  };

  const getColor = (bar) => {
    const name = bar.bar.data.name;
    const activeColor =
      bar.bar.data.value < 0 ? minusActiveColor : positiveActiveColor;
    const disableColor =
      bar.bar.data.value < 0 ? minusDisableColor : positiveDisableColor;

    if (selectedTopItems.length === 0) {
      return activeColor;
    }

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (selectedTopItems[i] === name) {
        return activeColor;
      }
    }

    return disableColor;
  };

  const handleBar = (event, bar) => {
    if (dragFlag) return;

    let _selectedTopItems;
    const name = bar.bar.data.name;

    if (event.shiftKey) {
      _selectedTopItems = selectedTopItems.slice();

      let index = NaN;
      for (let i = 0; i < _selectedTopItems.length; i++) {
        if (_selectedTopItems[i] === name) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        _selectedTopItems.push(name);
      } else {
        _selectedTopItems.splice(index, 1);
      }
    } else {
      if (selectedTopItems.includes(name) && selectedTopItems.length === 1) {
        _selectedTopItems = [];
      } else {
        _selectedTopItems = [name];
      }
    }

    if (!arraysEqual(selectedTopItems, _selectedTopItems)) {
      handleFilter({
        selectedTopItems: _selectedTopItems,
      });
    }
  };

  const handleLabel = (event, name) => {
    let _selectedTopItems;

    if (event.shiftKey) {
      _selectedTopItems = selectedTopItems.slice();

      let index = NaN;
      for (let i = 0; i < _selectedTopItems.length; i++) {
        if (_selectedTopItems[i] === name) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        _selectedTopItems.push(name);
      } else {
        _selectedTopItems.splice(index, 1);
      }
    } else {
      if (selectedTopItems.includes(name) && selectedTopItems.length === 1) {
        _selectedTopItems = [];
      } else {
        _selectedTopItems = [name];
      }
    }

    if (!arraysEqual(selectedTopItems, _selectedTopItems)) {
      handleFilter({
        selectedTopItems: _selectedTopItems,
      });
    }
  };

  const deSelectAll = () => {
    if (hoverBar) hoverBar.classList.remove('barHover');
    setHoverBar(null);
    if (selectedTopItems.length === 0) return;
    handleFilter({
      selectedTopItems: [],
    });
  };

  const showTooltipHandler = (event, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tData = {},
      top,
      left;

    top = event.clientY - 70;
    left =
      window.innerWidth / 2 < event.clientX
        ? event.clientX - 180
        : event.clientX + 30;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      tData = bar;
      let exist = false;

      selectedTopItems.forEach((topItem) => {
        if (topItem === bar.bar.data.name) {
          exist = true;
        }
      });

      if (exist) {
        let selectedData = chatData.filter((d) =>
          selectedTopItems.find((topItem) => topItem === d.name),
        );
        let sumValue = 0;
        selectedData.forEach((d) => (sumValue += d.value));
        tData = {
          bar: {
            data: {
              name: 'Multiple Selected',
              value: sumValue,
            },
          },
        };
      } else {
        tData = bar;
      }
    } else {
      tData['forecast'] = bar;
    }

    tData['isBar'] = isBar;

    showTooltip({
      tooltipData: tData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  };

  const hideTooltipHandler = () => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  };

  const width = Math.max(finiteNumber(window.innerWidth, 1024), 320);

  const wrapLabel = (labelText, maxCharsPerLine = 14, maxLines = 3) => {
    const source = (labelText || 'undefined').toString().trim();
    if (!source) return ['undefined'];

    const words = source.replace(/-/g, '- ').split(/\s+/).filter(Boolean);

    if (words.length === 0) return [source];

    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (candidate.length <= maxCharsPerLine || currentLine.length === 0) {
        currentLine = candidate;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) lines.push(currentLine);

    if (lines.length > maxLines) {
      const visibleLines = lines.slice(0, maxLines);
      const lastLine = visibleLines[maxLines - 1];
      visibleLines[maxLines - 1] =
        lastLine.length > maxCharsPerLine - 2
          ? `${lastLine.slice(0, maxCharsPerLine - 2)}..`
          : `${lastLine}..`;
      return visibleLines;
    }

    return lines;
  };

  const wrapPersonNameLabel = (labelText) => {
    const source = (labelText || 'undefined').toString().trim();
    if (!source) return ['undefined'];

    const commaParts = source
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

    if (commaParts.length >= 2) {
      const firstName = commaParts[1].split(/\s+/).filter(Boolean)[0];
      const lastName = commaParts[0];
      return firstName && lastName ? [firstName, lastName] : [source];
    }

    const words = source.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return [source];

    return [words[0], words.slice(1).join(' ')];
  };

  const getLabelText = (item) =>
    isPeopleProfitType(profitType) ? item.name : item.displayName || item.name;

  const getLabelLines = (item) =>
    isPeopleProfitType(profitType)
      ? wrapPersonNameLabel(getLabelText(item))
      : wrapLabel(getLabelText(item));

  const maxLabelLines = Math.max(
    1,
    ...chatData.map((item) => getLabelLines(item).length),
  );
  const labelLineHeight = 13;
  const height = 150 + Math.max(26, maxLabelLines * labelLineHeight + 8);

  const margin = {
    top: 10,
    right: 0,
    bottom: Math.max(46, maxLabelLines * labelLineHeight + 16),
    left: Math.max(finiteNumber(xTransform, 30), 0),
  };

  const params = getParams(window, width, height, margin);
  const pageYOffset = finiteNumber(params.pageYOffset, 0);
  const xMax = Math.max(
    finiteNumber(params.xMax, width - margin.left - margin.right),
    0,
  );
  let yMax = Math.max(
    finiteNumber(params.yMax, height - margin.top - margin.bottom),
    0,
  );

  const x = (d) => d.name;
  yMax = Math.max(yMax - 10, 1);
  const sortedChatData = [...chatData].sort((a, b) => -a.value + b.value);

  // scales
  const xScale = scaleBand()
    .domain(sortedChatData.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const safeMinData = finiteNumber(minData, 0);
  const safeMaxData = finiteNumber(maxData, 0);
  const yDomainMin = safeMinData > 0 ? 0 : safeMinData;
  const yDomainMax = safeMaxData < 0 ? 0 : safeMaxData;

  const yScale = scaleLinear({
    domain:
      yDomainMin === yDomainMax
        ? [yDomainMin - 1, yDomainMax + 1]
        : [yDomainMin, yDomainMax],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: ['value'],
    range: [positiveActiveColor],
  });

  return (
    <div id="pp_top">
      <div
        className="wrapper"
        style={{ display: 'flex', alignItems: 'center', height: 33 }}
      >
        <FormControlLabel
          control={
            <FormControl variant="standard">
              <Select
                sx={{
                  '& .MuiInputBase-input': {
                    padding: 0,
                    paddingRight: 24,
                    fontWeight: 'bold',
                    fontFamily: 'Verdana !important',
                    fontSize: '10pt',
                  },
                }}
                value={profitType}
                name="ProfitType"
                onChange={handleProfitTypeFilter}
              >
                <MenuItem value="Director">Director</MenuItem>
                <MenuItem value="ProjectManager">Project Leader</MenuItem>
                <MenuItem value="Supervisor">Associate</MenuItem>
                <MenuItem value="ProjectTypeDescription">Project Type</MenuItem>
                <MenuItem value="ProjectSubTypeDescription">
                  Project Sub Type
                </MenuItem>
                <MenuItem value="ProjectSourceOne">Project Source 1</MenuItem>
                <MenuItem value="ProjectSourceTwo">Project Source 2</MenuItem>
                <MenuItem value="ClientName">Client</MenuItem>
              </Select>
            </FormControl>
          }
          label="Profit By&nbsp;"
          labelPlacement="start"
          sx={{
            marginLeft: '0px !important',
            marginRight: '16px !important',
            '& .MuiTypography-root': {
              fontWeight: 'bold',
              fontFamily: 'Verdana !important',
              fontSize: '10pt',
            },
          }}
        />
        <div id="pp_year_selector">
          <YearSelector
            selectedYears={selectedYears}
            label={label}
            onChange={handleYear}
          />
        </div>
      </div>

      <Grid container>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
          onDragStart={(event) => event.preventDefault()}
          onMouseDown={onSvgMouseDown}
          onMouseMove={onSvgMouseMove}
          onMouseUp={onSvgMouseUp}
          onMouseLeave={onSvgMouseLeave}
        >
          <rect x={0} y={0} width={width} height={height} fill="transparent" />

          <Group
            top={finiteNumber(margin.top, 0)}
            left={finiteNumber(margin.left, 0)}
          >
            <AxisLeft
              numTicks={4}
              scale={yScale}
              stroke="black"
              tickStroke="black"
              strokeWidth={1}
              tickLabelProps={(value, index) => ({
                fill: 'black',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
                dx: '-0.33em',
              })}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text {...tickProps} fill="black">
                  ${formattedValue}
                </text>
              )}
            />

            <BarStack
              data={sortedChatData}
              keys={['value']}
              x={x}
              xScale={xScale}
              yScale={yScale}
              color={color}
            >
              {(barStacks) => {
                return barStacks.map((barStack) => {
                  return barStack.bars.map((bar) => {
                    return (
                      <rect
                        key={`bar-income-${barStack.index}-${bar.index}`}
                        className={
                          selectedTopItems.includes(bar.bar.data.name)
                            ? 'barActive'
                            : ''
                        }
                        data-no-drag="true"
                        x={finiteNumber(bar.x, 0)}
                        y={
                          isNaN(bar.height)
                            ? 0
                            : bar.height <= 0
                              ? finiteNumber(yScale(0), 0)
                              : finiteNumber(bar.y, 0)
                        }
                        height={
                          isNaN(bar.height)
                            ? 0
                            : bar.height < 0
                              ? finiteNumber(bar.y, 0) -
                                finiteNumber(yScale(0), 0)
                              : finiteNumber(bar.height, 0)
                        }
                        width={finiteNumber(bar.width, 0)}
                        fill={getColor(bar)}
                        onClick={(event) => handleBar(event, bar)}
                        onMouseLeave={hideTooltipHandler}
                        onMouseMove={(event) => showTooltipHandler(event, bar)}
                        onTouchEnd={hideTooltipHandler}
                        onTouchMove={(event) => showTooltipHandler(event, bar)}
                      />
                    );
                  });
                });
              }}
            </BarStack>

            <Bar
              width={finiteNumber(xMax, 0)}
              stroke="#d7d7d7"
              strokeWidth={1}
              height={1}
              y={finiteNumber(yScale(0), 0)}
            />

            <AxisBottom
              top={finiteNumber(yMax, 0)}
              scale={xScale}
              hideTicks={true}
              stroke="black"
              tickStroke="black"
            >
              {(axis) => {
                const tickLabelSize = 11;

                return (
                  <g className="my-custom-bottom-axis">
                    <Line
                      from={axis.axisFromPoint}
                      height={1}
                      to={axis.axisToPoint}
                      stroke="black"
                    />
                    {sortedChatData.map((item, i) => {
                      const bandStart = finiteNumber(xScale(item.name), 0);
                      const tickX = finiteNumber(
                        bandStart + xScale.bandwidth() / 2,
                        0,
                      );
                      const tickY = finiteNumber(
                        axis.axisToPoint.y + tickLabelSize + axis.tickLength,
                        tickLabelSize,
                      );
                      const tickColor = selectedTopItems.includes(item.name)
                        ? '#316ac5'
                        : 'black';
                      const lines = getLabelLines(item);

                      return (
                        <Group
                          key={`vx-tick-${item.name}-${i}`}
                          className="vx-axis-tick"
                        >
                          <text
                            data-no-drag="true"
                            x={tickX}
                            y={tickY}
                            fontSize={11}
                            textAnchor="middle"
                            dominantBaseline="hanging"
                            fill={tickColor}
                            onClick={(event) => handleLabel(event, item.name)}
                          >
                            {lines.map((line, lineIndex) => (
                              <tspan
                                key={`${item.name}-${lineIndex}`}
                                x={tickX}
                                dy={lineIndex === 0 ? 0 : labelLineHeight}
                              >
                                {line}
                              </tspan>
                            ))}
                          </text>
                        </Group>
                      );
                    })}
                  </g>
                );
              }}
            </AxisBottom>
            {dragFlag && (rW > 0 || rH > 0) && (
              <rect
                x={finiteNumber(rX, 0)}
                y={finiteNumber(rY, 0)}
                width={finiteNumber(rW, 0)}
                height={finiteNumber(rH, 0)}
                fill="lightblue"
                stroke="#00f3ff"
                strokeWidth={1}
                fillOpacity={0.2}
                pointerEvents="none"
              />
            )}
          </Group>
        </svg>

        {tooltipOpen && tooltipData && (
          <TooltipWithBounds
            top={finiteNumber(tooltipTop, 0) + pageYOffset}
            left={finiteNumber(tooltipLeft, 0)}
          >
            {tooltipData.isBar ? (
              <div className="bkg-white color-black">
                <div className="pdv-10">
                  <strong>{tooltipData.bar.data.name} </strong>
                </div>
                <div className="ft-12">
                  <strong>
                    Profit: {thousandFormat2(tooltipData.bar.data.value)}
                  </strong>
                </div>
              </div>
            ) : (
              <div className="bkg-white color-black">
                Sum IncomeMTDForecast HistoricalOnly ={' '}
                {thousandFormat(tooltipData.forecast)}
              </div>
            )}
          </TooltipWithBounds>
        )}
      </Grid>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  getProjectPerformanceSummary: PropTypes.func.isRequired,
  getProjectPerformanceDetail: PropTypes.func.isRequired,
  handleFilter: PropTypes.func.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  profitType: PropTypes.string,
};

export default TopChart;
